import { useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { isArchived } from '@/models/article';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { getFeesGroupedByType, shouldShowPendingOrganizationQuote } from '@/utils/price';

import {
  PendingQuoteBadge,
  PriceWithDetailsTooltipWithConversions,
} from './PriceWithDetails/PriceWithDetails';
import { FEE_TYPE_LABELS } from './PriceWithDetails/usePriceDetailsRows';

export const RequestOrganizationPrice = () => {
  const { request, view: requestView } = useRequestContext();
  const { t, i18n } = useLingui();

  const total = request.organizationPrice?.amountPerCurrency?.[0];
  const fees = getFeesGroupedByType(request, 'organization');

  const showTotalLine =
    !!total ||
    request.allArticles.some(
      (article) =>
        !isArchived(article) &&
        (article.snapshot.serviceChoice === 'care-repair' ||
          article.snapshot.serviceChoice === 'exchange')
    );

  if (fees.length === 0 && !showTotalLine) {
    return null;
  }

  const showAmountBeforeTaxes = requestView.organizationPrice.showAmountBeforeTaxes;
  const vatLabel = showAmountBeforeTaxes
    ? t({ id: '_general.excl-vat', message: 'excl. VAT' })
    : t({ id: '_general.incl-vat', message: 'incl. VAT' });
  const label = t({
    id: 'request-context.total-client-price',
    message: 'Total client price',
  });
  const showPendingQuote = request.articles.some((article) =>
    shouldShowPendingOrganizationQuote(article)
  );

  return (
    <Stack
      ariaLabel={label}
      className="bg-neutral-300"
      gap="0.25rem"
      padding="0.75rem"
      style={{ borderRadius: '0.5rem', flex: 1 }}
    >
      {fees.map((fee) => {
        const feeLabel = i18n._(FEE_TYPE_LABELS[fee.type]);

        return (
          <Stack
            key={fee.type}
            row
            justifyContent="space-between"
            alignItems="flex-start"
            gap="1rem"
            flexWrap="nowrap"
            ariaLabel={feeLabel}
          >
            <InlineText color="secondary">
              {feeLabel} ({vatLabel})
            </InlineText>
            <PriceWithDetailsTooltipWithConversions
              price={fee.price}
              direction="row-reverse"
              options={{
                showAmountBeforeTaxes: showAmountBeforeTaxes,
              }}
              variant="secondary"
            />
          </Stack>
        );
      })}
      {showTotalLine && (
        <Stack
          row
          justifyContent="space-between"
          alignItems="flex-start"
          gap="1rem"
          flexWrap="nowrap"
        >
          <InlineText>
            {label} ({vatLabel})
          </InlineText>
          {total ? (
            <PriceWithDetailsTooltipWithConversions
              price={total}
              direction="row-reverse"
              options={{
                showAmountBeforeTaxes: showAmountBeforeTaxes,
              }}
              variant="primary"
            />
          ) : showPendingQuote ? (
            <PendingQuoteBadge />
          ) : (
            <InlineText>-</InlineText>
          )}
        </Stack>
      )}
    </Stack>
  );
};
